import { useContext, useMemo } from 'react';
import { AcceptanceCriteriaTab } from '@features/components/CaseOverview/components/AcceptanceCriteria/AcceptanceCriteriaTab';
import { CaseBanners } from '@lp/features/CaseOverview/components/CaseBanners';
import { AuthContext } from '@lp/core/auth';
import { InvoicesSectionRow } from '@features/components/CaseOverview/components';
import { General, Donor, TxCenter, NotesSectionRow, LocationTracking } from './Sections';
import { SupplementalOrgans } from './Sections/SupplementalOrgans';
import { ServiceConfirmation } from './Sections/ServiceConfirmation';
import { SurgeonCaseSummaryCard } from './Sections/SurgeonCaseSummaryCard';
import { Case } from '@/api/apis/case';
import { getUnosId } from '@/utils/helpers';
import { useScrollToAnchor } from '@/hooks/useScrollToAnchor';
import { TxCenterConfirmationBanner } from './components/TxCenterConfirmationBanner';
import { InvoicePermissions } from '@/permissions';

type CaseDetailsTabProps = {
  caseInfo: Case;
  onTabChange: (tab: string) => void;
};

const CaseDetailsTab = ({ caseInfo, onTabChange }: CaseDetailsTabProps) => {
  const { checkPermission } = useContext(AuthContext);
  const hasViewInvoicePermission = checkPermission(InvoicePermissions.ReadInvoice);
  const {
    caseId,
    notes,
    resources,
    donorId,
    organType,
    reAllocationSequenceNumber,
    opoRequest,
    surgeonCaseSummary,
    status,
    txCenterServiceApprovalStatus,
    isExpeditedCase,
    invoices,
    laterality,
    duplicateCaseNumber,
  } = caseInfo;
  useScrollToAnchor();
  const surgeon = useMemo(() => {
    return resources.find((resource) => resource.resourceUserId === surgeonCaseSummary?.userId);
  }, [resources, surgeonCaseSummary]);
  const unosId = getUnosId(
    donorId,
    organType,
    reAllocationSequenceNumber,
    opoRequest,
    isExpeditedCase,
    laterality,
    duplicateCaseNumber,
  );

  const moveToLogs = () => onTabChange('log');

  return (
    <div className="flex flex-col gap-6">
      <div className="w-full flex flex-col gap-4">
        <TxCenterConfirmationBanner approvalStatus={txCenterServiceApprovalStatus} onDetailsClick={moveToLogs} />
        <CaseBanners />
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,400px),1fr))] gap-x-6 gap-y-8">
        <General caseData={caseInfo} />
        <Donor caseData={caseInfo} />
        <TxCenter caseData={caseInfo} />
        <AcceptanceCriteriaTab
          title="Requests and Acceptance Criteria"
          organType={caseInfo.organType}
          donorType={caseInfo.donorType}
          organAcceptanceCriteria={caseInfo.organAcceptanceCriteria}
        />
      </div>
      {caseInfo && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,400px),1fr))] gap-x-6 gap-y-8">
          <LocationTracking caseData={caseInfo} />
          <ServiceConfirmation caseData={caseInfo} />
          <SurgeonCaseSummaryCard
            unosId={unosId}
            organType={caseInfo.organType}
            surgeon={surgeon}
            surgeonCaseSummary={surgeonCaseSummary || undefined}
          />
          <SupplementalOrgans caseData={caseInfo} />
        </div>
      )}
      <NotesSectionRow caseId={caseId} notes={notes} status={status} />
      {hasViewInvoicePermission && <InvoicesSectionRow caseId={caseId} invoices={invoices} />}
    </div>
  );
};

export default CaseDetailsTab;
